import { LatLon } from "./gpsfunctions";
import Circle from "./Circle";
import DistanceMarker from "./DistanceMarker";
import Point from "./Point";

/**
 * Convert Latitude/Longitude degree to radium
 * @param {number} num
 * @returns number
 */
export function toRad(num) {
  return (Math.PI / 180) * num;
}

/**
 * Calculate distance between two Latitude-Longitude points
 * @param {YCLatLng | Point} p1
 * @param {YCLatLng | Point} p2
 * @returns number
 */
export function distance(p1, p2) {
  // return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
  const R = 6371000; // meters
  const dLat = toRad(p2.y - p1.y);
  const dLon = toRad(p2.x - p1.x);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(p1.y)) *
      Math.cos(toRad(p2.y)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
}

/**
 * @param {DistanceMarker[]} list
 * @returns number
 */
export function getLongestIndex(list) {
  let i;
  let biggestIndex = 0;
  for (i = 1; i < list.length; i += 1) {
    if (list[i].distance > list[biggestIndex].distance) {
      biggestIndex = i;
    }
  }
  return biggestIndex;
}

/**
 * @param {YCLatLng} point
 * @param {MapMarker[]} mapMarkers
 * @returns {DistanceMarker[]} At most 3 items with shortest distance from point.
 */
export function findShortestDistances(point, mapMarkers) {
  const markers = [];
  let longestIndex = -1;
  let i = 0;
  for (i = 0; i < mapMarkers.length; i += 1) {
    const d = distance(mapMarkers[i].realPoint, point);
    if (markers.length < 3) {
      markers.push(
        new DistanceMarker(
          mapMarkers[i].realPoint,
          mapMarkers[i].customPoint,
          d,
          i + 1
        )
      );
      longestIndex = getLongestIndex(markers);
    } else if (d < markers[longestIndex].distance) {
      markers[longestIndex] = new DistanceMarker(
        mapMarkers[i].realPoint,
        mapMarkers[i].customPoint,
        d,
        i + 1
      );
      longestIndex = getLongestIndex(markers);
    }
  }
  return markers;
}

/**
 * Returns intersection points for two circles
 * @param {Circle} c1
 * @param {Circle} c2
 * @returns {"" | Circle | Point | [Point,Point]}
 */
export function findIntersectionOfCircle(c1, c2) {
  // Calculate distance between centres of circle
  const d = distance(c1.center, c2.center);
  const c1r = c1.radius;
  const c2r = c2.radius;
  const m = c1r + c2r;
  let n = c1r - c2r;

  if (n < 0) {
    n *= -1;
  }

  // No solns
  if (d > m) {
    return "";
  }

  // Circle are contained within each other
  if (d < n) {
    if (c1r < c2r) {
      return c1;
    }
    return c2;
  }

  // Circles are the same
  // eslint-disable-next-line eqeqeq
  if (d == 0 && c1r == c2r) {
    return c1;
  }

  const a = (c1r * c1r - c2r * c2r + d * d) / (2 * d);
  const h = Math.sqrt(c1r * c1r - a * a);

  // Calculate point p, where the line through the circle intersection points crosses the line between the circle centers.
  const p = new Point();
  p.x = c1.center.x + (a / d) * (c2.center.x - c1.center.x);
  p.y = c1.center.y + (a / d) * (c2.center.y - c1.center.y);

  let gps = new LatLon(c1.center.y, c1.center.x);
  const newP1 = gps.destPoint(
    LatLon.bearing(c1.center.y, c1.center.x, c2.center.y, c2.center.x),
    a / 1000
  );

  // 1 soln , circles are touching
  // eslint-disable-next-line eqeqeq
  if (d == c1r + c2r) {
    return new Point(newP1.x, newP1.y);
  }
  // 2solns
  let p1 = new Point();
  let p2 = new Point();
  gps = new LatLon(newP1.y, newP1.x);
  p1 = gps.destPoint(
    LatLon.bearing(c1.center.y, c1.center.x, newP1.y, newP1.x) + 90,
    h / 1000
  );
  p2 = gps.destPoint(
    LatLon.bearing(c1.center.y, c1.center.x, newP1.y, newP1.x) - 90,
    h / 1000
  );

  return [new Point(p1.x, p1.y), new Point(p2.x, p2.y)];
}

/**
 * @param {Point[]} vertices
 * @returns {Point}
 */
export function getCenterOfShape(vertices) {
  if (vertices.length === 2) {
    return LatLon.midPoint(
      vertices[0].y,
      vertices[0].x,
      vertices[1].y,
      vertices[1].x
    );
  }
  let i;
  let sumx = 0;
  let sumy = 0;
  for (i = 0; i < vertices.length; i += 1) {
    sumx += vertices[i].x;
    sumy += vertices[i].y;
  }
  return new Point(sumx / i, sumy / i);
}

/**
 * @param {Circle} c1
 * @param {Circle} c2
 * @param {number} multiplyer
 * @returns {Point}
 */
export function find2CirclesCenter(c1, c2, multiplyer) {
  // eslint-disable-next-line no-param-reassign
  c1.radius *= multiplyer;
  // eslint-disable-next-line no-param-reassign
  c2.radius *= multiplyer;
  const i12 = findIntersectionOfCircle(c1, c2);
  // eslint-disable-next-line eqeqeq
  if (i12 == "") {
    return find2CirclesCenter(c1, c2, 1.1);
  }
  if (i12 instanceof Circle) {
    return i12.center;
  }
  if (i12 instanceof Point) {
    return i12;
  }
  let smallCircle = c2;
  let largeCircle = c1;
  if (c1.radius < c2.radius) {
    smallCircle = c1;
    largeCircle = c2;
  }
  // We are trying to get the point on the perimeter of the smaller circle that also lies on
  // the line connecting the midpoint of the intersections and the center of the small circle.
  // The two intersection points and the newly calculated point will form a triangle. The center
  // of the triangle will be the center of the overall intersection.

  // Get the midpoint for the two intersection points
  const midpoint = getCenterOfShape([i12[0], i12[1]]);
  // Get the angle of the line connecting the midpoint and the center of the small circle
  const angle = Math.atan(
    (midpoint.y - smallCircle.center.y) / (midpoint.x - smallCircle.center.x)
  );
  // Calculate the length for the line right across from the angle and parellel to Y-axis
  const r2d = 180 / Math.PI;
  const d2r = Math.PI / 180;
  const conversionlat = (smallCircle.radius / 6371000) * r2d; //  using 6371000 as earth's radius
  const conversionlng = conversionlat / Math.cos(smallCircle.center.y * d2r);
  const p1 = new Point();
  p1.y = smallCircle.center.y + conversionlat * Math.sin(angle);
  p1.x = smallCircle.center.x + conversionlng * Math.cos(angle);

  const p2 = new Point();
  p2.y = smallCircle.center.y - conversionlat * Math.sin(angle);
  p2.x = smallCircle.center.x - conversionlng * Math.cos(angle);
  if (distance(p1, largeCircle.center) < largeCircle.radius) {
    return getCenterOfShape([i12[0], i12[1], p1]);
  }
  return getCenterOfShape([i12[0], i12[1], p2]);
}

export function insideCircle(circle, point) {
  const d = distance(circle.center, point);
  return d <= circle.radius;
}

/**
 * @param {DistanceMarker[]} markers
 * @param {number} multiplyer
 * @returns {Point}
 */
export function find3CirclesCenter(markers, multiplyer) {
  const c1 = markers[0].getCircle(multiplyer);
  const c2 = markers[1].getCircle(multiplyer);
  const c3 = markers[2].getCircle(multiplyer);

  const i12 = findIntersectionOfCircle(c1, c2);
  if (i12 === "") {
    return find3CirclesCenter(markers, multiplyer * 1.1);
  }
  if (i12 instanceof Circle) {
    return find2CirclesCenter(i12, c3, 1);
  }
  const i23 = findIntersectionOfCircle(c2, c3);
  if (i23 === "") {
    return find3CirclesCenter(markers, multiplyer * 1.1);
  }
  if (i23 instanceof Circle) {
    return find2CirclesCenter(i23, c1, 1);
  }
  const i13 = findIntersectionOfCircle(c1, c3);
  if (i13 === "") {
    return find3CirclesCenter(markers, multiplyer * 1.1);
  }
  if (i13 instanceof Circle) {
    return find2CirclesCenter(i13, c2, 1);
  }

  const intersectionPoints = [];

  if (i12 instanceof Point) {
    if (insideCircle(c3, i12)) {
      intersectionPoints.push(i12);
    } else {
      return find3CirclesCenter(markers, multiplyer * 1.1);
    }
  } else {
    // eslint-disable-next-line vars-on-top
    const i1 = insideCircle(c3, i12[0]);
    // eslint-disable-next-line vars-on-top
    const i2 = insideCircle(c3, i12[1]);
    if (!i1 && !i2) {
      return find3CirclesCenter(markers, multiplyer * 1.1);
    }
    if (i1) {
      intersectionPoints.push(i12[0]);
    }
    if (i2) {
      intersectionPoints.push(i12[1]);
    }
  }

  if (i23 instanceof Point) {
    if (insideCircle(c1, i23)) {
      intersectionPoints.push(i23);
    } else {
      return find3CirclesCenter(markers, multiplyer * 1.1);
    }
  } else {
    // eslint-disable-next-line vars-on-top
    const i1 = insideCircle(c1, i23[0]);
    // eslint-disable-next-line vars-on-top
    const i2 = insideCircle(c1, i23[1]);
    if (!i1 && !i2) {
      return find3CirclesCenter(markers, multiplyer * 1.1);
    }
    if (i1) {
      intersectionPoints.push(i23[0]);
    }
    if (i2) {
      intersectionPoints.push(i23[1]);
    }
  }

  if (i13 instanceof Point) {
    if (insideCircle(c2, i13)) {
      intersectionPoints.push(i13);
    } else {
      return find3CirclesCenter(markers, multiplyer * 1.1);
    }
  } else {
    // eslint-disable-next-line vars-on-top
    const i1 = insideCircle(c2, i13[0]);
    // eslint-disable-next-line vars-on-top
    const i2 = insideCircle(c2, i13[1]);
    if (!i1 && !i2) {
      return find3CirclesCenter(markers, multiplyer * 1.1);
    }
    if (i1) {
      intersectionPoints.push(i13[0]);
    }
    if (i2) {
      intersectionPoints.push(i13[1]);
    }
  }

  if (intersectionPoints.length === 1) {
    return intersectionPoints[0];
  }
  if (intersectionPoints.length === 2) {
    return getCenterOfShape([intersectionPoints[0], intersectionPoints[1]]);
  }
  if (intersectionPoints.length === 3) {
    return getCenterOfShape([
      intersectionPoints[0],
      intersectionPoints[1],
      intersectionPoints[2],
    ]);
  }
  if (intersectionPoints.length === 2) {
    return getCenterOfShape([
      intersectionPoints[0],
      intersectionPoints[1],
      intersectionPoints[2],
      intersectionPoints[3],
    ]);
  }
  return getCenterOfShape(intersectionPoints);
}

const stripTags = (str = "") => {
  return str
    .replace(/<b>/g, "")
    .replace(/<\/b>/g, "")
    .replace(/<\/div>/g, "")
    .replace(/<div.*>/g, ", ");
};

/**
 * @param {google.maps.LatLng} start
 * @param {google.maps.LatLng[]} stopovers
 * @param {google.maps.LatLng} end
 * @param {*} callback
 * @param {*} errorCallback
 */
export function calculateDirections(
  start,
  stopovers,
  end,
  callback,
  errorCallback
) {
  const waypoints = stopovers.map((stopover) => ({
    location: stopover,
    stopover: true,
  }));
  const directionsService = new window.google.maps.DirectionsService();
  directionsService.route(
    {
      origin: start,
      destination: end,
      travelMode: window.google.maps.TravelMode.WALKING,
      waypoints,
    },
    // eslint-disable-next-line func-names
    function (res, status) {
      const paths = [];
      const instructions = [];

      if (status !== "OK") {
        errorCallback("Path not found.");
        return;
      }

      // sanity checks and create path
      if (res.routes && res.routes.length) {
        const { legs } = res.routes[0];
        for (let e = 0; e < legs.length; e += 1) {
          const path = [];
          // Get our current steps [DirectionsStep]
          // See: https://developers.google.com/maps/documentation/javascript/directions#Steps
          // There is a ton of details Like estimated time... etc.
          const { steps } = legs[e];
          if (steps?.length) {
            // loop through our steps
            for (let s = 0; s < steps.length; s += 1) {
              const changedInstructions = stripTags(
                steps[s].instructions
              ).split(", ");
              const lastInstruction = changedInstructions[1]
                ? `, ${changedInstructions[1]}`
                : "";

              instructions.push(
                `${changedInstructions[0]} and walk
                  ${steps[s].distance?.text} for
                  ${steps[s].duration?.text}${lastInstruction}`
              );
              // DirectionsStep.start_location contains the geocoded LatLng of the starting point of this step
              path.push(steps[s].start_location);
              // DirectionStep.path contains an array of LatLngs describing the course of this step
              const subpath = steps[s].path;
              // loop through our "middle" lat lngs
              for (let t = 0; t < subpath?.length; t += 1) {
                path.push(subpath[t]);
              }
              // DirectionStep.end_location contains the LatLng of the ending point of this step
              path.push(steps[s].end_location);
            }
            paths.push(path);
          }
        }
      }

      // success - return path
      callback(paths, instructions);
    }
  );
}
